
import React, { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ImagePreview } from './ImagePreview';
import { Camera, Upload } from 'lucide-react';
import { UploadProgress } from '@/utils/imageHandling';
import { validateFile } from '@/utils/fileValidation';
import { toast } from 'sonner';

interface ImageUploadProps {
  id: string;
  label: string;
  onChange: (file: File, isMature?: boolean) => void;
  preview: string | null;
  className?: string;
  isLoading?: boolean;
  error?: string | null;
  type: 'avatar' | 'banner';
  uploadProgress?: UploadProgress;
  isPremiumUser?: boolean;
}

export const ImageUpload = ({
  id,
  label,
  onChange,
  preview,
  className,
  isLoading,
  error,
  type,
  uploadProgress,
  isPremiumUser = false
}: ImageUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processFile = async (file: File) => {
    if (!file) return;
    
    setIsValidating(true);
    try {
      // Only allow image files
      if (!file.type.startsWith('image/')) {
        console.error('Invalid file type:', file.type);
        toast.error('Please upload an image file (JPG, PNG, HEIC)');
        return;
      }
      
      // Use our central validation utility
      const validatedResult = await validateFile(file, isPremiumUser);
      // Pass both the file and its mature status to the parent component
      onChange(validatedResult.file, validatedResult.isMature);
    } catch (error) {
      console.error('File validation error:', error);
      
      // Different handling for content moderation vs technical errors
      if (error instanceof Error && error.message.includes('guidelines')) {
        toast.error(error.message, {
          duration: 8000,
          important: true
        });
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to validate image');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      processFile(file);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      processFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const getProgressColor = () => {
    if (uploadProgress?.state === 'error') return 'bg-red-500';
    if (uploadProgress?.state === 'success') return 'bg-green-500';
    return 'bg-primary';
  };

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className={cn(
          "block text-sm font-medium text-gray-200",
          error && "text-red-500"
        )}
      >
        {label}
      </label>

      <div
        className={cn(
          "relative group cursor-pointer",
          type === 'avatar' 
            ? 'w-24 h-24'
            : 'w-full aspect-[16/9] min-h-[160px]',
          className
        )}
        onDragEnter={handleDrag}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          id={id}
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="hidden"
          disabled={isLoading || isValidating}
        />

        <div
          className={cn(
            "w-full h-full overflow-hidden transition-all",
            type === 'avatar' 
              ? 'rounded-full ring-2 ' + (dragActive ? 'ring-primary' : 'ring-primary/20')
              : 'rounded-lg border border-[#343536] bg-[#151516]',
            (isLoading || isValidating) && "opacity-70",
            error && "border-red-500/50"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <ImagePreview
            src={preview}
            alt={`${type} preview`}
            type={type}
            fallback={
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                {type === 'avatar' ? (
                  <Camera className="w-6 h-6 text-gray-400" />
                ) : (
                  <Upload className="w-6 h-6 text-gray-400" />
                )}
                <span className="text-sm text-gray-400 font-medium">
                  {isValidating ? 'Validating...' : `Upload ${type === 'avatar' ? 'Avatar' : 'Banner'}`}
                </span>
              </div>
            }
          />
          
          {uploadProgress && uploadProgress.state === 'uploading' && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="w-full max-w-[80%] h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={cn("h-full transition-all", getProgressColor())}
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
            </div>
          )}
          
          {isValidating && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="text-sm text-white">Validating image...</div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};
